import type { Field } from './fields';

export enum ModuleConnectionDisplayMode {
  Grid = 'Grid',
  Carousel = 'Carousel',
}

export enum ModuleBucketSize {
  Full = 'Full',
  Half = 'Half',
  Compact = 'Compact',
  TwoThirds = 'TwoThirds',
}

export type ModuleConnectionLayout = 'gallery' | 'data' | 'image-fields' | 'compact';

export type SessionModuleDisplaySettings = {
  connectionsDisplayMode: ModuleConnectionDisplayMode;
  moduleBucketSize: ModuleBucketSize;
  moduleRowSpan?: number; // 1 = single row (default), 2+ = span multiple rows
  connectionLayout?: ModuleConnectionLayout; // undefined = auto-infer from source
};

export const moduleColors = [
  '#fc5c65',
  '#fd9644',
  '#fed330',
  '#26de81',
  '#2bcbba',
  '#45aaf2',
  '#4b7bec',
  '#a55eea',
  '#d1d8e0',
  '#778ca3',
];

export enum SessionPermissions {
  EDIT = 'edit',
  VIEW = 'view',
  // TODO: is this level necessary
  PRIVATE = 'private',
}

export interface SessionModule {
  id: string;
  name: string;
  color: string;
  sourceId: string;
  multi: boolean;
  table: boolean;
  fields: string[];
  /**
   * @deprecated Use fields array with attachmentFieldId instead.
   * Kept for backwards compatibility — existing sessions may have this set.
   * When reading: if showThumbnails is true and attachmentFieldId not in fields, treat as visible.
   * When writing: add attachmentFieldId to fields instead of setting this.
   */
  showThumbnails?: boolean;
}

/**
 * Step definition for Multi-Step Queue Infrastructure
 * Steps represent stages in a workflow (e.g., "Pre-Production", "Shooting", "Post-Production")
 */
export interface SessionStep {
  id: string;
  name: string;
  position: number; // For ordering (0, 1, 2, ...)
}

/**
 * Per-step layout override. When set for a step, these values take precedence over the
 * session-global moduleDisplaySettings / visibility / cardTitleFormula.
 * Absence of a field means "inherit from global" — no override needed for every step.
 */
export interface StepLayoutOverride {
  moduleDisplaySettings?: Record<string, SessionModuleDisplaySettings>;
  hiddenModuleIds?: string[];
  cardTitleFormula?: Field[];
}

export interface FilenameSettings {
  disableFilenameCleanup: boolean;
  disableSpecialCharacterCleanup: boolean;
  fieldSeparator: string;
  filenameCaps: boolean;
  cardNumberWidth: number;
  spaceSeparator: string;
  specialCharacterReplacement: string;
  trimSpaces: boolean;
  trimSpecialCharacters: boolean;
}

export interface FirestoreSession {
  name: string;
  createdAt: Date;
  lastOpenedAt?: Date;
  createdBy: {
    userId: string;
    email: string | null;
  };
  deletedAt?: Date;
  /** Set on soft-delete; restore uses this to put `isArchived` back to prior Done/Active state */
  wasDoneBeforeDelete?: boolean;
  isArchived?: boolean;
  userId: string;
  organizationId?: string; // NEW: Optional for backward compatibility
  cardTitleFormula: Field[];
  filenameFormula: Field[];
  filenameSettings: FilenameSettings;
  automaticModuleCreation: boolean;
  automaticCardTitleSuffix: boolean;
  automaticFilenameVersioning: boolean;
  filenameParametersUpdatedAt?: Date;
  sourceIds: string[];
  modules: Record<string, SessionModule>;
  moduleIds: string[];
  cardIds: string[];
  visibility: {
    hiddenModuleIds: string[];
    hideFilename: boolean;
  };
  moduleDisplaySettings: Record<string, SessionModuleDisplaySettings>;
  publicAccessLevel: SessionPermissions;
  timeSavedInSeconds?: number;
  // NEW: Sharing configuration
  sharing?: {
    mode: 'private' | 'org' | 'direct';
    // Org-scoped sharing
    organizationId?: string;
    teamIds?: string[]; // If sharing with specific teams
    // Direct sharing
    externalSharingEnabled?: boolean; // Only if org allows it
    // Legacy compatibility
    publicAccessLevel?: SessionPermissions; // Deprecated, kept for migration
  };

  // Multi-Step Queue Infrastructure
  // Steps define workflow stages (e.g., "Pre-Production", "Shooting", "Post-Production")
  // When empty/undefined, falls back to legacy binary queue (In Progress / Done)
  steps?: SessionStep[];

  // Per-step layout overrides. Keyed by stepId.
  // When set, overrides session-global moduleDisplaySettings / visibility / cardTitleFormula
  // for cards currently in that step.
  stepLayoutOverrides?: Record<string, StepLayoutOverride>;

  assetTabPreferences?: {
    defaultSourceIds?: string[];
  };
}
